import React, { createContext, useContext, useState, useEffect } from 'react';
import type { AuthenticationResponseJSON, PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import api from '../services/api';

interface User {
  id: string;
  email: string;
  name: string;
  isMember: boolean;
  isAdmin?: boolean;
  membershipPlanId?: string;
  referralCode?: string;
  referralPoints?: number;
  isVerified?: boolean;
}

export type LoginResult =
  | { user: User }
  | { requiresPasskey: true; options: PublicKeyCredentialRequestOptionsJSON; passkeyToken: string };

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  completePasskeyLogin: (passkeyToken: string, response: AuthenticationResponseJSON) => Promise<User>;
  register: (
    email: string,
    password: string,
    name: string,
    options?: { referralCode?: string }
  ) => Promise<void>;
  setSession: (user: User) => void;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    api
      .get('/auth/me')
      .then(response => {
        if (response.data?.user) {
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
      })
      .catch(() => {
        setUser(null);
        localStorage.removeItem('user');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const setSession = (sessionUser: User) => {
    setUser(sessionUser);
    localStorage.setItem('user', JSON.stringify(sessionUser));

    const guestId = localStorage.getItem('guestChatId');
    if (guestId) {
      api
        .post('/chats/me/claim-guest', { guestId })
        .then(() => {
          localStorage.removeItem('guestChatId');
        })
        .catch(() => {
          // Keep guest id if claim fails
        });
    }
  };

  const login = async (email: string, password: string): Promise<LoginResult> => {
    const response = await api.post('/auth/login', { email, password });
    const data = response.data;
    if (data.requiresPasskey && data.options && data.passkeyToken) {
      return {
        requiresPasskey: true,
        options: data.options,
        passkeyToken: data.passkeyToken,
      };
    }
    if (data.user) {
      setSession(data.user);
      return { user: data.user };
    }
    throw new Error('Invalid login response');
  };

  const completePasskeyLogin = async (passkeyToken: string, response: AuthenticationResponseJSON): Promise<User> => {
    const res = await api.post('/auth/passkey/authenticate', { passkeyToken, response });
    const { user } = res.data;
    setSession(user);
    return user;
  };

  const register = async (
    email: string,
    password: string,
    name: string,
    options?: { referralCode?: string }
  ) => {
    const response = await api.post('/auth/register', {
      email,
      password,
      name,
      referralCode: options?.referralCode,
    });
    if (response.data?.user) {
      setSession(response.data.user);
    }
  };

  const logout = () => {
    api.post('/auth/logout').catch(() => {});
    setUser(null);
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ user, login, completePasskeyLogin, register, setSession, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

